import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';
import App from './App';
import { cacheKeys } from './constants/storageKeys';
import './index.css';
import { PCStoreJsBridge } from './nativeapi';
import rootReducer from './reducers';
import rootSaga from './sagas/index';
import { registerToken, logInit, logNative } from './utils/utils';
import KeepAlive, { AliveScope } from 'react-activation';
import { registerMicroApps, addGlobalUncaughtErrorHandler, start, prefetchApps } from 'qiankun';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { CaptureConsole as CaptureConsoleIntegration } from '@sentry/integrations';

import {
  CONTEXT,
  MICROCONTEXT,
  APP_VERSION,
  APP_VERSION_CODE,
  APP_VERSION_D,
  APP_VERSION_CODE_D,
  FORUM_ENTRY,
} from './config';

// const releasStr = `pc-stroe-h5@${process.env.REACT_APP_ENV}-${APP_VERSION_D}`;
const releasStr = `pc-app-store-h5@${CONTEXT.slice(1, 50)}-${APP_VERSION_D}`;
if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    release: releasStr,
    dsn: 'https://fd824ba6ca1d47408eda8df451abba3d@sentry-lb.lenovo.com.cn/45',
    integrations: [
      new Integrations.BrowserTracing(),
      new CaptureConsoleIntegration({
        // array of methods that should be captured
        // defaults to ['log', 'info', 'warn', 'error', 'debug', 'assert']
        levels: ['error'],
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: '0.001',
  });
}

const sagaMiddleware = createSagaMiddleware();
const store = createStore(rootReducer, composeWithDevTools(applyMiddleware(sagaMiddleware)));

sagaMiddleware.run(rootSaga);

// 模拟器计时器对象
window.SIMTime = {};
// 开启是否可以拖动大小
window.PcstoreCanChangeSize = true;
// JS下载完成
window.PC_STORE_START_JS_LOAD_END = +new Date();

// 接收来自 native 的消息
window.CallJsProtoMethod = message => {
  const msg = PCStoreJsBridge.base64ToArrayBuffer(message);
  // 发布消息
  PCStoreJsBridge.publishMessage(msg);
  // 并把消息同步给子项目
  if (window.FormCallJsProtoMethod) {
    window.FormCallJsProtoMethod(message);
  }
};
if (!window.CallNativeMethod) {
  window.CallNativeMethod = () => {};
}

/************* 子应用的一些数据处理 *************/
if (!window.mainHistory) {
  window.mainHistory = () => {};
}
if (!window.mainInstallSoft) {
  window.mainInstallSoft = () => {};
}
if (!window.mainDeleteMsgHot) {
  window.mainDeleteMsgHot = () => {};
}
if (!window.mainGetLogInfoData) {
  window.mainGetLogInfoData = () => {};
}
if (!window.mainSetLogInfoData) {
  window.mainSetLogInfoData = () => {};
}
if (!window.mainForumNotice) {
  window.mainForumNotice = () => {};
}
const mainCallbackFun = params => {
  window.FormCallJsProtoMethod = params.FormCallJsProtoMethod;
};
/*************  *************/

// 提前去获取机器信息和注册请求需要的TOKEN(暂定7天过期)
const machineInfoStr = localStorage.getItem(cacheKeys.LOCALMACHINE_INFO);
const token = localStorage.getItem(cacheKeys.REGISTER_TOKEN);
if (!machineInfoStr) {
  PCStoreJsBridge.getLocalMachineInfo().then(res => {
    logInit(res); // 拿到machineInfo就去注册打桩
    if (!token) {
      registerToken(res); // 拿着machineInfo注册token
    }
  });
} else {
  const machineInfo = JSON.parse(machineInfoStr);
  logInit(machineInfo); // 拿到machineInfo就去注册打桩
  if (!token) {
    registerToken(machineInfo); // 拿着machineInfo注册token
  }
}

// 还是重新获取一次machineInfo，避免区分独立包打桩问题。
PCStoreJsBridge.getLocalMachineInfo().then(res => {
  localStorage.setItem(cacheKeys.LOCALMACHINE_INFO, JSON.stringify(res));
});

// 提前去获取StartInfo（sessionId）后面好用
PCStoreJsBridge.getStartInfo();

ReactDOM.render(
  <Provider store={store}>
    <AliveScope>
      <App />
    </AliveScope>
  </Provider>,
  document.getElementById('root'),
);

const apps = [
  {
    name: 'ForumApp',
    entry: FORUM_ENTRY,
    container: '#apps',
    activeRule: () => {
      return true;
    },
    props: {
      mainInfo: { CONTEXT, APP_VERSION, APP_VERSION_CODE, APP_VERSION_D, APP_VERSION_CODE_D },
      mainCallbackFun: mainCallbackFun,
      mainInstallSoft: window.mainInstallSoft,
      mainCefQuery: window.cefQuery,
      mainCallNativeMethod: window.CallNativeMethod,
      mainHistory: window.mainHistory,
      mainDeleteMsgHot: window.mainDeleteMsgHot,
      mainGetLogInfoData: window.mainGetLogInfoData,
      mainSetLogInfoData: window.mainSetLogInfoData,
      mainForumNotice: window.mainForumNotice,
    },
  },
];

prefetchApps([{ name: 'ForumApp', entry: FORUM_ENTRY }]);
/**
 * 注册微应用
 * 第一个参数 - 微应用的注册信息
 * 第二个参数 - 全局生命周期钩子
 */
registerMicroApps(apps, {
  // qiankun 生命周期钩子 - 微应用加载前
  beforeLoad: app => {
    logNative('qiankun 生命周期钩子 - 微应用加载前 before load', app.name);
  },
  // qiankun 生命周期钩子 - 微应用挂载后
  afterMount: app => {
    logNative('qiankun 生命周期钩子 - 微应用挂载后 after mount', app.name);
  },
});

/**
 * 添加全局的未捕获异常处理器
 */
addGlobalUncaughtErrorHandler(event => {
  logNative('qiankun 生命周期钩子 - 全局的未捕获异常处理器', event);
  const { message: msg } = event;
  // 加载失败时提示
  if (msg && msg.includes('died in status LOADING_SOURCE_CODE')) {
    logNative(
      'qiankun 生命周期钩子 - 全局的未捕获异常处理器 微应用加载失败，请检查应用是否可运行',
      msg,
    );
  }
});

// start({ sandbox: { experimentalStyleIsolation: true } });
start({ singular: false });
